import {useRef} from 'react'
import round from 'lodash/round.js'

import cn from '../../className.js'
import {HK_CANCEL} from '../../constants/HotKeys.js'
import {triggerHotKey} from '../HotKeys.js'
import {isNumeric, isPresent} from '../../utils.js'
import useAutoFocus from '../../hooks/useAutoFocus.js'

function isInBounds(value, min = -Infinity, max = Infinity) {
  return value <= max && value >= min
}

function increment({value, onIncrement, onChange, min, max}) {
  const newValue = round(Number(value || '0') + 1, 3)

  if (isNumeric(newValue) && isInBounds(newValue, min, max)) {
    if (onIncrement) {
      onIncrement(newValue)
    } else if (onChange) {
      onChange(newValue)
    }
  }
}

function decrement({value, onDecrement, onChange, min, max}) {
  const newValue = round(Number(value || '0') - 1, 3)

  if (isNumeric(newValue) && isInBounds(newValue, min, max)) {
    if (onDecrement) {
      onDecrement(newValue)
    } else if (onChange) {
      onChange(newValue)
    }
  }
}

/**
 * Component used to capture numbers
 *
 * @param {Object} props
 * @param {String} [props.id]
 * @param {Number|String} [props.value]
 * @param {String} [props.label]
 * @param {String} [props.labelClassName]
 * @param {Boolean} [props.isInvalid]
 * @param {Function} [props.onChange]
 * @param {Function} [props.onIncrement]
 * @param {Function} [props.onDecrement]
 * @param {Function} [props.onBlur]
 * @param {Function} [props.onEnterKeyPress]
 * @param {Number} [props.min]
 * @param {Number} [props.max]
 * @param {Boolean} [props.autoFocus]
 * @param {Boolean} [props.required]
 * @param {Boolean} [props.disabled]
 * @param {String} [props.containerClassName]
 * @param {String} [props.className]
 * @param {String} [props.errorMessage]
 * @param {'sm'|'md'|'lg'} [props.size] The overall size of the input
 * @param {Number} [props.widthPerCharacter] Width per character (rem) assumes 1rem extra
 * @param {String} [props.minWidth] Minimum width of input element
 * @param {String} [props.placeholder]
 * @returns {React.ReactNode}
 */
export default function NumberInput({
  id,
  value,
  label,
  labelClassName,
  isInvalid,
  onChange,
  onIncrement,
  onDecrement,
  onBlur,
  onEnterKeyPress,
  min,
  max,
  autoFocus,
  required,
  disabled,
  containerClassName,
  className,
  errorMessage,
  size = 'sm',
  widthPerCharacter = size === 'lg' ? 1.35 : size === 'md' ? 0.95 : 0.45,
  minWidth = size === 'lg' ? '3rem' : size === 'md' ? '2.5rem' : '2rem',
  placeholder,
}) {
  const elementRef = useRef()
  isInvalid = (isInvalid || !!errorMessage) && !disabled

  useAutoFocus(elementRef, autoFocus)

  const element = (
    <>
      {label && id && (
        <label className={labelClassName} htmlFor={id}>
          <span>{label}</span>
          {required ? <span className="required">*</span> : null}
        </label>
      )}
      <div className={cn`flex ${containerClassName}`}>
        <input
          type="text"
          id={id || ''}
          className={cn`input--x-sm input--w-suffix margin-bottom-0 ${{
            'input--error': isInvalid,
          }} ${className}`}
          value={isPresent(value) ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp') {
              increment({value, onIncrement, onChange, min, max})
            }
            if (event.key === 'ArrowDown') {
              decrement({value, onDecrement, onChange, min, max})
            }
            if (event.key === 'Enter' && onEnterKeyPress) {
              onEnterKeyPress()
            }
            if (event.key === 'Escape') {
              triggerHotKey(HK_CANCEL)
            }
          }}
          onBlur={onBlur ? (event) => onBlur(event.target.value) : null}
          disabled={disabled}
          style={
            widthPerCharacter !== null || minWidth !== null
              ? {
                  width: `${(value || '').toString().length * widthPerCharacter + 1}rem`,
                  minWidth,
                }
              : {}
          }
          ref={elementRef}
          placeholder={placeholder}
        />
        <div className="wrap--suffix-group flex--col">
          <button
            className="btn btn--suffix btn--arrow-up flex-1"
            type="button"
            onClick={() => increment({value, onIncrement, onChange, min, max})}
            tabIndex="-1"
            disabled={disabled}
          />
          <button
            className="btn btn--suffix btn--arrow-down flex-1"
            type="button"
            onClick={() => decrement({value, onDecrement, onChange, min, max})}
            tabIndex="-1"
            disabled={disabled}
          />
        </div>
      </div>
      {errorMessage && (
        <small className="error error-message">{errorMessage}</small>
      )}
    </>
  )

  if (['lg', 'md'].includes(size)) {
    return (
      <div
        className={
          size === 'lg'
            ? 'wrap--lg-number-input'
            : size === 'md'
              ? 'wrap--md-number-input'
              : ''
        }
      >
        {element}
      </div>
    )
  }

  return element
}
